import React from 'react'
import {BoxLink} from '../../common/router_links/BoxLink'
import {CompartmentLink} from '../../common/router_links/CompartmentLink'
import {ProductLink} from '../../common/router_links/ProductLink'
import {exhaustSwitchCase} from '../../common/utils/exhaustSwitchCase'
import {resolveBoxDisplayName} from '../../common/utils/resolveBoxDisplayName'
import {resolveProductStatusDisplayName} from '../../common/utils/resolveProductStatusDisplayName'
import {Box} from '../../webui/Box'
import {TableHeadCell} from '../../webui/table/sortable-paginated-table/TableHeadCell'
import {Order} from '../../webui/table/sortable-paginated-table/types'
import {Comparator, descendingComparator, getComparator, getNumericalComparator} from '../../webui/table/sortable-paginated-table/utils'
import {LockedCell} from './LockedCell'
import {CompartmentsThatNeedsActionData} from './utils/createCompartmentsThatNeedsActionData'

export const INITIAL_ROWS_PER_PAGE = 40

const getCompartmentComparator = (order: Order, a: CompartmentsThatNeedsActionData, b: CompartmentsThatNeedsActionData) =>
  getNumericalComparator<CompartmentsThatNeedsActionData['compartment']>(order, 'name')(a.compartment, b.compartment)

export const compartmentsThatNeedsActionComparator: Comparator<CompartmentsThatNeedsActionData> = (order, orderBy) => {
  switch (orderBy) {
    case 'box':
      return (a, b) => {
        const number = getNumericalComparator<CompartmentsThatNeedsActionData['box']>(order, 'name')(a.box, b.box)
        if (number === 0) {
          return getCompartmentComparator('asc', a, b)
        }
        return number
      }
    case 'contractType':
      return (a, b) => {
        const aa = String(a.contractType ?? '')
        const bb = String(b.contractType ?? '')
        return order === 'desc' ? descendingComparator(aa, bb) : -descendingComparator(aa, bb)
      }
    case 'compartment':
      return (a, b) => getCompartmentComparator(order, a, b)
    case 'product':
      return (a, b) =>
        order === 'desc' ? descendingComparator(a.product?.name, b.product?.name) : -descendingComparator(a.product?.name, b.product?.name)
    case 'productStatus':
      break
    case 'id':
      break
    case 'organisationId':
      break
    case 'date':
      break
    case 'locked':
      return (a, b) => (order === 'asc' ? Number(a.locked) - Number(b.locked) : Number(b.locked) - Number(a.locked))
    default:
      return exhaustSwitchCase(orderBy)
  }
  return getComparator(order, orderBy)
}

export const createCompartmentsThatNeedsActionTableHeadCells = (args: {
  contractTypeLabel: (t?: any) => string
}): TableHeadCell<CompartmentsThatNeedsActionData>[] => [
  {
    id: 'box',
    label: 'Box',
    render: (row) => <BoxLink boxId={row.box.id} linkText={resolveBoxDisplayName(row.box)} />,
    search: (row, id, searchTerm) => {
      return String(resolveBoxDisplayName(row.box)).toLowerCase().includes(searchTerm.toLowerCase())
    },
  },
  {
    id: 'contractType',
    label: 'Contract type',
    render: (row) => <Box>{args.contractTypeLabel(row.contractType ?? null)}</Box>,
  },
  {
    id: 'compartment',
    label: 'Comp.',
    render: (value) => <CompartmentLink boxId={value.box.id} compartmentId={value.compartment.id} linkText={value.compartment.name ?? ''} />,
    maxWidth: 50,
  },
  {
    id: 'product',
    label: 'Product',
    render: (value) => (value.product ? <ProductLink product={value.product} /> : 'No product'),
  },
  {
    id: 'productStatus',
    label: 'Status',
    render: (value) => (value.productStatus ? resolveProductStatusDisplayName(value.productStatus) : ''),
  },
  {
    id: 'locked',
    label: 'Locked?',
    disableRowLink: true,
    render: (value) => (
      <LockedCell
        locked={value.locked}
        latestLockEventDate={value.compartment.latestLockEvent?.date}
        latestLockEventLockedBy={value.compartment.latestLockEvent?.lockedBy}
      />
    ),
  },
]
